import { z } from 'zod'

/**
 * Single source of truth for input rules the frontend and backend must agree
 * on. Deliberately carries only the *rule* (type/min/max/format) plus a plain
 * English fallback message — no i18n, no frontend-specific presentation
 * metadata (labelKey). The backend uses these messages directly (it has no
 * i18n system); the frontend's own `core/validation/` runner derives its own
 * localized copy from each field's `.meta({labelKey})` — which callers layer
 * on top of these bare schemas — and never reads a zod issue's raw `.message`
 * at all, so these messages are harmless, unused weight on that path.
 *
 * This file is the template's core contract only — auth, roles, principals,
 * the notification/push/presence envelopes. A feature adds its own schemas
 * beside these, in this same file (both sides must agree on them), never by
 * hand-duplicating the rule in the feature's own package.
 */
export const credentialsSchema = z.object({
  email: z.string().trim().min(1, 'Email is required.').email('Email is not a valid address.'),
  password: z.string().min(1, 'Password is required.'),
})

// The minimum for a *new* password (registration, self-service change). Login
// still accepts `min(1)` via credentialsSchema — an account created under an
// older rule must keep working.
export const MIN_PASSWORD_LENGTH = 10

// Public registration is gated by a shared invite code the admin hands out;
// admins create accounts directly and never need one. Drop `inviteCode`
// here (and its matching field on the register form) for an app that wants
// open signup instead.
export const registerSchema = z.object({
  email: z.string().trim().min(1, 'Email is required.').email('Email is not a valid address.'),
  password: z.string().min(MIN_PASSWORD_LENGTH, `Password must be at least ${MIN_PASSWORD_LENGTH} characters.`),
  inviteCode: z.string().min(1, 'An invite code is required.'),
})

export const refreshSchema = z.object({
  token: z.string().min(1, 'Token is required.'),
})

// A user's own display name — what every screen shows instead of their raw
// email. null (or blank, which the backend normalises to null) means "not
// set", and callers fall back to the email.
export const updateProfileSchema = z.object({
  displayName: z.string().trim().max(80, 'Display name is too long.').nullable(),
})

// Self-service password change. `newPassword` follows the same minimum as
// registration; the current one only has to be non-empty (it is checked
// against the stored hash, not re-validated).
export const changePasswordSchema = z.object({
  currentPassword: z.string().min(1, 'Enter your current password.'),
  newPassword: z.string().min(MIN_PASSWORD_LENGTH, `Password must be at least ${MIN_PASSWORD_LENGTH} characters.`),
})

/**
 * What an actor may do at all, independent of any particular object. The
 * backend gates every operation on one of these; the frontend uses them to
 * decide which pages to *offer*, which is display only — authorization is
 * always re-checked server-side.
 *
 * Shared because both sides name them. Which capabilities a role carries is
 * deliberately *not* shared: that is a deployment decision the server owns.
 *
 * Only the core/admin capabilities live here — a feature adds its own
 * `{domain}.{verb}` entries to this enum when it needs display gating.
 */
export const capabilitySchema = z.enum([
  'users.manage',
  'access.inspect',
  'errors.read',
  'audit.read',
  'authorization.bypass',
])
export type Capability = z.infer<typeof capabilitySchema>

// Roles both sides must agree on: the backend stores and enforces them, the
// frontend only displays them (nav gating is UX, never authorization).
export const userRoleSchema = z.enum(['user', 'admin'])
export type UserRole = z.infer<typeof userRoleSchema>

// The roles an administrator may hand out through the admin console.
export const assignableRoleSchema = z.enum(['user', 'admin'])
export type AssignableRole = z.infer<typeof assignableRoleSchema>

export const setRoleSchema = z.object({
  role: assignableRoleSchema,
})

export const setPasswordSchema = z.object({
  password: z.string().min(1, 'Password is required.'),
})

// A principal is who a grant, an invite, or an inbox item names — a user or a
// team. Kept generic (not tied to any one resource kind) so a feature's own
// sharing/assignment surface can reuse it instead of inventing its own.
export const principalSchema = z.discriminatedUnion('kind', [
  z.object({ kind: z.literal('user'), email: z.string().trim().min(1, 'Email is required.') }),
  z.object({ kind: z.literal('team'), teamId: z.string().min(1, 'Team is required.') }),
])
export type Principal = z.infer<typeof principalSchema>

export const userPrincipalSchema = z.object({
  kind: z.literal('user'),
  email: z.string().trim().min(1, 'Email is required.'),
})
export type UserPrincipal = z.infer<typeof userPrincipalSchema>

/**
 * GET /inbox — one item in the notification bell, newest first. `kind` is a
 * discriminated union so the frontend can key rows without special-casing
 * which kind it got; a feature adds its own kind to the union rather than
 * repurposing an existing one. `resourceType` is a free-form tag (not an
 * enum) on purpose — the core template has no resource kinds of its own to
 * enumerate; a feature names its own tags when it starts writing inbox rows.
 */
export const genericInboxItemSchema = z.object({
  kind: z.literal('generic'),
  itemId: z.string(),
  resourceType: z.string(),
  resourceId: z.string(),
  resourceLabel: z.string(),
  createdBy: z.string(),
  createdAt: z.string(),
})
export const inboxItemSchema = z.discriminatedUnion('kind', [genericInboxItemSchema])
export type GenericInboxItemDto = z.infer<typeof genericInboxItemSchema>
export type InboxItemDto = z.infer<typeof inboxItemSchema>
export const inboxItemListSchema = z.array(inboxItemSchema)

// A Web Push subscription's public half, exactly as PushManager.subscribe()
// hands it back (core/services/service-worker.gateway.ts) — the private key
// material never leaves the browser. Validated here so a malformed body from
// a future non-browser caller fails loud at the route, not deep inside the
// encryption call.
export const pushSubscriptionKeysSchema = z.object({
  p256dh: z.string().min(1, 'A p256dh key is required.'),
  auth: z.string().min(1, 'An auth secret is required.'),
})

export const createPushSubscriptionSchema = z.object({
  endpoint: z.string().trim().url('The endpoint must be a valid URL.'),
  keys: pushSubscriptionKeysSchema,
})
export type CreatePushSubscription = z.infer<typeof createPushSubscriptionSchema>

// A membership is one account's standing *inside one workspace* — the
// tenant-scoping boundary the template carries as reusable multi-tenant
// infra (core/infra/workspace/, core/infra/authentication/). One login can
// hold many memberships; the workspace roles form a ladder
// owner ⊇ manager ⊇ member ⊇ guest. Distinct from UserRole, which is the
// platform-wide standing.
export const workspaceRoleSchema = z.enum(['owner', 'manager', 'member', 'guest'])
export type WorkspaceRole = z.infer<typeof workspaceRoleSchema>

export const workspaceMembershipSchema = z.object({
  workspaceId: z.string(),
  email: z.string(),
  role: workspaceRoleSchema,
  addedAt: z.string(),
  addedBy: z.string().nullable(),
})
export type WorkspaceMembershipDto = z.infer<typeof workspaceMembershipSchema>
export const workspaceMembershipListSchema = z.array(workspaceMembershipSchema)

// What GET /me carries about the caller's memberships — the workspace name is
// joined in so a switcher needs no second request. `personal` marks the
// workspace minted with the account (cannot be left).
export const meMembershipSchema = z.object({
  workspaceId: z.string(),
  name: z.string(),
  role: workspaceRoleSchema,
  personal: z.boolean(),
})
export type MeMembershipDto = z.infer<typeof meMembershipSchema>
export const meMembershipListSchema = z.array(meMembershipSchema)

export const createWorkspaceSchema = z.object({
  name: z.string().trim().min(1, 'Workspace name is required.').max(100, 'Workspace name is too long.'),
})

export const renameWorkspaceSchema = z.object({
  name: z.string().trim().min(1, 'Workspace name is required.').max(100, 'Workspace name is too long.'),
})

// A guest's chosen display name (POST /guest) — prefilled by the frontend
// with a generated placeholder, editable before the player commits to it.
// Kept short: it appears next to a 6-character game code in a shared UI.
export const guestNameSchema = z.object({
  name: z.string().trim().min(1, 'A name is required.').max(24, 'Name is too long.'),
})
export type GuestName = z.infer<typeof guestNameSchema>

// `null` means untimed — today's only behaviour, kept as the default so an
// older client/server that predates timers still agrees on "no clock".
// Both counts in whole seconds (the wire's own unit; the client converts to
// ms only where it needs sub-second display maths).
export const timeControlSchema = z
  .object({
    initialSeconds: z.number().int().positive(),
    incrementSeconds: z.number().int().min(0),
  })
  .nullable()
export type TimeControl = z.infer<typeof timeControlSchema>

// What `POST /games` takes: the host's name (unchanged) plus their chosen
// time control. A separate schema from `guestNameSchema` — that one is also
// `POST /guest`'s own shape, which has no time control of its own. Defaulted
// to `null` (untimed) so a request that omits it — every caller before this
// field existed — still parses.
export const createGameSchema = guestNameSchema.extend({
  timeControl: timeControlSchema.default(null),
})
export type CreateGame = z.infer<typeof createGameSchema>

// The Duke is strictly 2 players (see rulebook) — a lobby's only job is
// pairing exactly two sessions onto one game before the board is set up.
export const GAME_CODE_LENGTH = 6

export const joinGameSchema = z.object({
  code: z.string().trim().toUpperCase().length(GAME_CODE_LENGTH, `Enter the ${GAME_CODE_LENGTH}-character game code.`),
})

// 'waiting-for-opponent' until a second player joins by code; 'ready' once
// both seats are filled and the Duke's own setup phase (side choice is fixed,
// placing Duke + Footmen) is open; 'playing' once both players have placed;
// 'finished' once a Duke has been captured. See `gameStateSchema` below for
// the setup/play-phase payload — out of scope for `gameSummarySchema`, which
// the lobby alone still parses.
export const gameStatusSchema = z.enum(['waiting-for-opponent', 'ready', 'playing', 'finished'])
export type GameStatus = z.infer<typeof gameStatusSchema>

export const gameSummarySchema = z.object({
  gameId: z.string(),
  code: z.string(),
  status: gameStatusSchema,
  hostName: z.string(),
  // null until the second player has joined.
  guestName: z.string().nullable(),
  // Visible from creation on — the lobby and the sidebar's active-games list
  // both want to show it before play starts.
  timeControl: timeControlSchema,
})
export type GameSummaryDto = z.infer<typeof gameSummarySchema>

// One row of `GET /games` (the signed-in account's own open games) — the
// lobby's own summary plus the two fields a sidebar entry needs to label
// itself ("vs Bob — your turn") and route correctly (Setup vs. Play).
export const activeGameSummarySchema = gameSummarySchema.extend({
  yourSeat: z.enum(['host', 'guest']),
  activeSeat: z.enum(['host', 'guest']).nullable(),
})
export type ActiveGameSummaryDto = z.infer<typeof activeGameSummarySchema>

// The Duke's board: fixed 6x6, host's home row is the last row, guest's is
// row 0. The standard 15-kind roster and the full Move/Slide/Jump/Jump
// Slide/Command/Strike icon vocabulary are implemented (`duke-moves.ts`,
// `duke-tiles.ts`, `duke-bag.ts`); terrain, Defense, Dread and the Enhanced
// tiles (Oracle, Duchess) remain deliberately out of scope.
export const BOARD_SIZE = 6

export const boardSquareSchema = z.object({
  row: z
    .number()
    .int()
    .min(0)
    .max(BOARD_SIZE - 1),
  col: z
    .number()
    .int()
    .min(0)
    .max(BOARD_SIZE - 1),
})
export type BoardSquare = z.infer<typeof boardSquareSchema>

// The standard box's 15 distinct troop-tile kinds (rulebook p.1's Contents
// list, minus the Enhanced tiles Duchess/Oracle) — see `duke-tiles.ts` for
// each kind's movement grids.
export const tileKindSchema = z.enum([
  'duke',
  'footman',
  'pikeman',
  'bowman',
  'champion',
  'general',
  'knight',
  'marshall',
  'priest',
  'seer',
  'wizard',
  'assassin',
  'dragoon',
  'longbowman',
  'ranger',
])
export type TileKind = z.infer<typeof tileKindSchema>

// Every tile has two faces; setup always places the starting one.
export const tileSideSchema = z.enum(['starting', 'non-starting'])
export type TileSide = z.infer<typeof tileSideSchema>

// Which of the two fixed seats — never chosen, always host-vs-guest.
export const seatSchema = z.enum(['host', 'guest'])
export type Seat = z.infer<typeof seatSchema>

export const placedTileSchema = boardSquareSchema.extend({
  kind: tileKindSchema,
  side: tileSideSchema,
  owner: seatSchema,
})
export type PlacedTileDto = z.infer<typeof placedTileSchema>

// A board *is* its occupied squares — an empty square is simply an absent
// entry, never a null placeholder in an array of 36.
export const boardSchema = z.array(placedTileSchema)

// One atomic submission: Duke plus exactly two Footmen. Submitting all three
// at once is what makes "a player may not set up twice" a single server-side
// boolean instead of a partial-placement state machine.
export const setupPlacementSchema = z.object({
  duke: boardSquareSchema,
  footmen: z.tuple([boardSquareSchema, boardSquareSchema]),
})
export type SetupPlacement = z.infer<typeof setupPlacementSchema>

// The four things a turn can be — one endpoint each
// (`POST /games/:id/move|command|strike|draw`), so each body stays the plain
// shape its own endpoint already had rather than growing a `type` tag no
// existing caller sends. `src/shared/duke-moves.ts` (`applyTurn`) is the sole
// referee of whether a given one is legal for a given board and seat;
// `src/shared/duke-bag.ts` covers `draw`'s legal squares.
export const moveRequestSchema = z.object({
  from: boardSquareSchema,
  to: boardSquareSchema,
})
export type MoveRequest = z.infer<typeof moveRequestSchema>

export const commandRequestSchema = z.object({
  commander: boardSquareSchema,
  from: boardSquareSchema,
  to: boardSquareSchema,
})
export type CommandRequest = z.infer<typeof commandRequestSchema>

export const strikeRequestSchema = z.object({
  striker: boardSquareSchema,
  target: boardSquareSchema,
})
export type StrikeRequest = z.infer<typeof strikeRequestSchema>

export const drawRequestSchema = z.object({
  at: boardSquareSchema,
})
export type DrawRequest = z.infer<typeof drawRequestSchema>

// The full live-game payload `features/game/` parses. Deliberately a
// *separate* schema from `gameSummarySchema` rather than an extension of it
// in place: the lobby's three `gameSummarySchema.safeParse` calls must keep
// accepting the same wire shape they always have. `.extend()` still shares
// the five common fields' definitions once; zod strips the extra keys when
// `gameSummarySchema` parses the same response.
export const gameStateSchema = gameSummarySchema.extend({
  board: boardSchema,
  hostSetupDone: z.boolean(),
  guestSetupDone: z.boolean(),
  // Resolved server-side from the caller's token — null for a viewer who
  // holds neither seat. The client never decides its own seat.
  yourSeat: seatSchema.nullable(),
  // The three fields below are all null before `status` reaches 'playing' —
  // there is no seat to move yet, no move has happened, and nobody has won.
  activeSeat: seatSchema.nullable(),
  winner: seatSchema.nullable(),
  lastMove: z.object({ from: boardSquareSchema, to: boardSquareSchema }).nullable(),
  // Counts only — bag contents stay server-side so a client can't see what's
  // left to draw. Both null until `status` reaches 'playing', same as
  // `activeSeat`/`winner`/`lastMove` above.
  bagCount: z.object({ host: z.number().int().min(0), guest: z.number().int().min(0) }).nullable(),
  // Only ever the viewer's own pending draw (`toGameStateDto` never leaks the
  // opponent's) — the square is still the player's own choice, so only the
  // *kind* is revealed here, never a coordinate.
  pendingDraw: z.object({ kind: tileKindSchema }).nullable(),
  // `null` whenever `timeControl` is `null` (untimed). Each seat's own
  // remaining time in ms, plus the epoch-ms moment the *current* turn
  // started — `null` before `status` reaches 'playing' (no turn is running
  // yet). The client derives a live countdown from `turnStartedAtMs` between
  // polls; the server is still the one and only place a clock can expire.
  clock: z
    .object({
      host: z.number().int().min(0),
      guest: z.number().int().min(0),
      turnStartedAtMs: z.number().int().nullable(),
    })
    .nullable(),
})
export type GameStateDto = z.infer<typeof gameStateSchema>

// One ranked cross-domain hit (GET /search). No detail beyond what a picker
// needs to render and navigate — the domain's own read endpoint is still the
// source of truth for anything more. `type` is free-form; a feature that adds
// itself to the search index also adds itself to the frontend's
// `ROUTE_FOR_TYPE` map (core/infra/search/search.repository.ts).
export const searchHitSchema = z.object({
  type: z.string(),
  id: z.string(),
  label: z.string(),
  sublabel: z.string(),
})
export type SearchHit = z.infer<typeof searchHitSchema>
